import React, { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../context/globalContext";
import { NavLink } from "react-router-dom";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import {
  getRegistrationCatalog,
  RegistrationCatalog,
  MembershipTier,
} from "../../api/registration";
import getSymbolFromCurrency from "currency-symbol-map";
import PricingWindowPanel from "./PricingWindowPanel";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import CancellationPolicy from "../../components/CancellationPolicy/CancellationPolicy";

const TIER_ROWS: { tier: MembershipTier; label: string }[] = [
  { tier: "ieee_student_life_retired", label: "IEEE Student / Life / Retired" },
  { tier: "ieee_member", label: "IEEE Member" },
  { tier: "non_ieee_member", label: "Non-IEEE Member" },
];

const WINDOW_COLS = [
  { key: "earlyBird" as const, label: "Early Bird" },
  { key: "standard" as const, label: "Standard" },
  { key: "onSite" as const, label: "On-Site" },
] as const;

type WindowKey = (typeof WINDOW_COLS)[number]["key"];

function formatUsd(amount: number): string {
  return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function tierMap<T extends { membershipTier: MembershipTier }>(rows: T[]) {
  return Object.fromEntries(rows.map((row) => [row.membershipTier, row]));
}

function PriceCell({
  amount,
  isActive,
  muted,
}: {
  amount: string;
  isActive?: boolean;
  muted?: boolean;
}) {
  return (
    <td
      className={`px-4 py-3 text-right tabular-nums ${
        isActive
          ? "bg-amber-50 font-semibold text-slate-900"
          : muted
            ? "text-slate-400"
            : "text-slate-700"
      }`}
    >
      {amount}
    </td>
  );
}

function FullConferenceTable({
  catalog,
  fullByTier,
}: {
  catalog: RegistrationCatalog;
  fullByTier: Record<string, RegistrationCatalog["fullConference"][0]>;
}) {
  const activeWindow = catalog.activePricingWindow as WindowKey | null;

  return (
    <section className="mb-10">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-slate-900">Full Conference</h3>
        <p className="text-sm text-slate-500 mt-1">
          Rate depends on when you register. The highlighted column is the
          current pricing period.
          {catalog.banquet
            ? " Banquet dinner is not included — purchase separately as an add-on."
            : ""}
        </p>
        <p className="text-sm text-slate-600 mt-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2">
          <strong>Registering a paper?</strong> Choose Full Conference and provide
          your Paper ID and Paper title (required).{" "}
          <strong>Not registering a paper?</strong> Choose Listener.
        </p>
      </div>

      <div className="overflow-x-auto rounded-sm border border-slate-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3 text-left font-medium text-slate-600 w-[40%]">
                Delegate type
              </th>
              {WINDOW_COLS.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-right font-medium whitespace-nowrap ${
                    activeWindow === col.key
                      ? "text-amber-900 bg-amber-50/80"
                      : "text-slate-600"
                  }`}
                >
                  <span className="block">{col.label}</span>
                  {activeWindow === col.key && (
                    <span className="block text-[10px] font-semibold uppercase tracking-wide text-amber-700 mt-0.5">
                      Current
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {TIER_ROWS.map(({ tier, label }) => {
              const rates = fullByTier[tier]?.ratesUsd;
              return (
                <tr key={tier} className="bg-white">
                  <td className="px-4 py-3 font-medium text-slate-800">{label}</td>
                  {WINDOW_COLS.map((col) => (
                    <PriceCell
                      key={col.key}
                      amount={rates ? formatUsd(rates[col.key]) : "—"}
                      isActive={activeWindow === col.key}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ListenerTable({
  singleByTier,
}: {
  singleByTier: Record<string, RegistrationCatalog["singleDay"][0]>;
}) {
  return (
    <section className="mb-10">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-slate-900">Listener</h3>
        <p className="text-sm text-slate-500 mt-1">
          Fixed rate — same price regardless of registration period. Banquet
          dinner is not included — purchase separately as an add-on.
        </p>
      </div>

      <div className="overflow-x-auto rounded-sm border border-slate-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3 text-left font-medium text-slate-600 w-[70%]">
                Delegate type
              </th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">
                Rate (USD)
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {TIER_ROWS.map(({ tier, label }) => {
              const row = singleByTier[tier];
              const price = row ? row.pricePence / 100 : null;
              return (
                <tr key={tier} className="bg-white">
                  <td className="px-4 py-3 font-medium text-slate-800">{label}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                    {price != null ? formatUsd(price) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const PAPER_PLUS_TIER_ROWS: { tier: MembershipTier; label: string }[] = [
  { tier: "ieee_member", label: "IEEE Member" },
  { tier: "non_ieee_member", label: "Non-IEEE Member" },
];

function FormatPriceTable({
  title,
  description,
  notice,
  rates,
  symbol,
}: {
  title: string;
  description: string;
  notice?: string;
  rates: NonNullable<RegistrationCatalog["formatRates"]>["fullConference"];
  symbol: string;
}) {
  const byTierFormat = (tier: MembershipTier, format: "in_person" | "virtual") =>
    rates.find(
      (r) => r.membershipTier === tier && r.participationFormat === format,
    );

  return (
    <section className="mb-10">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500 mt-1">{description}</p>
        {notice && (
          <p className="text-sm text-slate-600 mt-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2">
            {notice}
          </p>
        )}
      </div>
      <div className="overflow-x-auto rounded-sm border border-slate-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3 text-left font-medium text-slate-600 w-[40%]">
                Delegate type
              </th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">
                Onsite (USD)
              </th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">
                Virtual (USD)
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {PAPER_PLUS_TIER_ROWS.map(({ tier, label }) => {
              const onsite = byTierFormat(tier, "in_person");
              const virtual = byTierFormat(tier, "virtual");
              return (
                <tr key={tier} className="bg-white">
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {label}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                    {onsite
                      ? `${symbol}${(onsite.pricePence / 100).toFixed(0)}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                    {virtual
                      ? `${symbol}${(virtual.pricePence / 100).toFixed(0)}`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PaperPlusAddonRateTables({
  catalog,
  symbol,
}: {
  catalog: RegistrationCatalog;
  symbol: string;
}) {
  const full = catalog.formatRates?.fullConference ?? [];
  const addOn = catalog.formatRates?.addOn ?? [];
  const singleDay = catalog.formatRates?.singleDay ?? [];

  return (
    <>
      <FormatPriceTable
        title="Full Registration"
        description="Price varies by IEEE membership and onsite vs virtual attendance. Each full registration covers one paper."
        notice="Registering a paper requires a Full Registration. Additional people under the same paper purchase an Additional Person add-on and must provide name, email, and IEEE Member ID when applicable."
        rates={full}
        symbol={symbol}
      />
      {singleDay.length > 0 && (
        <FormatPriceTable
          title="Listener"
          description="For attendees who are not registering a paper."
          rates={singleDay}
          symbol={symbol}
        />
      )}
      <FormatPriceTable
        title="Additional Person (add-on)"
        description="Required when more than one person registers under the same paper. Priced by membership and attendance format."
        rates={addOn}
        symbol={symbol}
      />
      {catalog.testTicket && (
        <section className="mb-10">
          <div className="overflow-x-auto rounded-sm border border-amber-200">
            <table className="min-w-full text-sm">
              <tbody>
                <tr className="bg-amber-50/60">
                  <td className="px-4 py-3">
                    <span className="font-medium text-amber-950">
                      {catalog.testTicket.name}
                    </span>
                    <p className="text-xs text-amber-800 mt-0.5">
                      For Stripe checkout testing only
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-amber-950 tabular-nums">
                    {symbol}
                    {(catalog.testTicket.pricePence / 100).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function AddOnsSection({
  banquet,
  extraPageFee,
  testTicket,
  additionalPaperFee,
  symbol,
}: {
  banquet: RegistrationCatalog["banquet"];
  extraPageFee: RegistrationCatalog["extraPageFee"];
  testTicket: RegistrationCatalog["testTicket"];
  additionalPaperFee?: RegistrationCatalog["additionalPaperFee"];
  symbol: string;
}) {
  const additionalPaperPricePence = additionalPaperFee?.pricePence ?? 38000;

  if (!banquet && !testTicket && !extraPageFee && !additionalPaperFee) return null;

  return (
    <section className="mb-10">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-slate-900">Add-ons</h3>
        <p className="text-sm text-slate-500 mt-1">
          Optional extras you can add during registration. Conference packages
          do not include the banquet dinner.
        </p>
      </div>

      <div className="overflow-x-auto rounded-sm border border-slate-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3 text-left font-medium text-slate-600 w-[70%]">
                Add-on
              </th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">
                Price
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr className="bg-white">
              <td className="px-4 py-3 text-slate-800">
                Additional paper (in-person full conference only)
                <p className="text-xs text-slate-500 mt-0.5 font-normal">
                  Same flat rate for all IEEE membership types
                </p>
              </td>
              <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                {symbol}
                {(additionalPaperPricePence / 100).toFixed(0)} per paper
              </td>
            </tr>
            {banquet && (
              <tr className="bg-white">
                <td className="px-4 py-3 text-slate-800">Banquet Ticket</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                  {symbol}
                  {(banquet.pricePence / 100).toFixed(0)} per ticket
                </td>
              </tr>
            )}
            {extraPageFee && (
              <tr className="bg-white">
                <td className="px-4 py-3 text-slate-800">
                  Extra pages (over {extraPageFee.includedPages} pages)
                  <p className="text-xs text-slate-500 mt-0.5 font-normal">
                    Full conference paper registration
                  </p>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular-nums">
                  {symbol}
                  {(extraPageFee.pricePerPagePence / 100).toFixed(0)} per page
                </td>
              </tr>
            )}
            {testTicket && (
              <tr className="bg-amber-50/60">
                <td className="px-4 py-3">
                  <span className="font-medium text-amber-950">{testTicket.name}</span>
                  <p className="text-xs text-amber-800 mt-0.5">
                    For Stripe checkout testing only
                  </p>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-amber-950 tabular-nums">
                  {symbol}
                  {(testTicket.pricePence / 100).toFixed(2)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface Props {
  eventSlug: string;
  registrationOpen?: boolean;
}

export default function ConferenceRateTable({
  eventSlug,
  registrationOpen = true,
}: Props) {
  const { currentUser } = useContext(GlobalContext);
  const [catalog, setCatalog] = useState<RegistrationCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedOnce = useRef(false);

  // Do not wait for isAuthLoading: fetchWithAuth already attaches any stored
  // token, and the catalog API is optionalAuthenticate. Waiting for auth made
  // cold /:slug visits much slower than home → click (where auth was already done).
  useEffect(() => {
    let cancelled = false;
    if (!hasLoadedOnce.current) setIsLoading(true);
    getRegistrationCatalog(eventSlug)
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setError(null);
          hasLoadedOnce.current = true;
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            getUserFacingErrorMessage(
              err,
              "Failed to load registration rates.",
            ),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventSlug, currentUser?.id, currentUser?.role]);

  useEffect(() => {
    hasLoadedOnce.current = false;
  }, [eventSlug]);

  if (isLoading) {
    return (
      <LoadingPanel
        title="Loading registration rates"
        subtitle="Retrieving pricing windows and delegate options."
      />
    );
  }

  if (error || !catalog) {
    return <ErrorAlert>{error ?? "Rates unavailable."}</ErrorAlert>;
  }

  const isPaperPlusAddon = catalog.registrationPolicy === "paper_plus_addon";
  const currency =
    (
      catalog.formatRates?.fullConference[0]?.currency ??
      catalog.fullConference[0]?.currency
    )?.toUpperCase() ?? "USD";
  const symbol = getSymbolFromCurrency(currency) ?? "$";
  const fullByTier = tierMap(catalog.fullConference);
  const singleByTier = tierMap(catalog.singleDay);

  return (
    <div className="eh-card">
      <SectionHeader
        subtitle="Registration"
        header="Rates & packages"
        description={
          isPaperPlusAddon
            ? "Rates vary by IEEE membership and onsite vs virtual attendance. Each full registration covers one paper; additional people under the same paper purchase an add-on."
            : catalog.banquet
              ? "Choose full conference or listener access. IEEE members receive discounted rates. Banquet tickets are not included and can be purchased separately at registration."
              : "Choose full conference or listener access. IEEE members receive discounted rates."
        }
      />

      {!isPaperPlusAddon && !catalog.banquet && (
        <div className="mb-6 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Banquet dinner tickets are not currently available for purchase.
        </div>
      )}

      {catalog.testPricing?.enabled && catalog.testPricing.label && (
        <div className="mb-6 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {catalog.testPricing.label}
        </div>
      )}

      {!isPaperPlusAddon && catalog.pricingWindowOptions && (
        <PricingWindowPanel options={catalog.pricingWindowOptions} />
      )}

      {isPaperPlusAddon ? (
        <PaperPlusAddonRateTables catalog={catalog} symbol={symbol} />
      ) : (
        <>
          <FullConferenceTable catalog={catalog} fullByTier={fullByTier} />
          <ListenerTable singleByTier={singleByTier} />
          <AddOnsSection
            banquet={catalog.banquet}
            extraPageFee={catalog.extraPageFee}
            testTicket={catalog.testTicket}
            additionalPaperFee={catalog.additionalPaperFee}
            symbol={symbol}
          />
        </>
      )}

      {eventSlug === "ieee-dtpi-2026" && (
        <div className="mt-8 mb-4">
          <CancellationPolicy />
        </div>
      )}

      {registrationOpen && (
        <div className="flex justify-center pt-2 border-t border-slate-100">
          <NavLink
            to={`/events/${eventSlug}/register`}
            className="eh-btn-primary px-10 mt-6"
          >
            Register for this event
          </NavLink>
        </div>
      )}
    </div>
  );
}
